import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { playNotificationSound } from '../../common/audioUtils';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiPackage, FiEdit2, FiImage, FiDollarSign, FiTag, FiShoppingBag } = FiIcons;

const AddProductFromUpcModal = ({ isOpen, onClose, initialData }) => {
  const { addProduct, adjustInventory } = useInventory();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    manufacturer: '',
    model: '',
    name: '',
    description: '',
    upc: '',
    localSku: '',
    localPrice: '',
    category: '',
    image: '',
    specifications: {},
    retailers: []
  });

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  // Track if image loaded successfully in preview
  const [imageLoadError, setImageLoadError] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        manufacturer: initialData.manufacturer || '',
        model: initialData.model || '',
        name: initialData.name || '',
        description: initialData.description || '',
        upc: initialData.upc || '',
        localSku: initialData.localSku || initialData.upc || '',
        localPrice: initialData.localPrice || initialData.averagePrice || '',
        category: initialData.category || '',
        image: initialData.image || '',
        specifications: initialData.specifications || {},
        retailers: initialData.retailers || []
      });
      // Reset image error state when new data comes in
      setImageLoadError(false);
    }
  }, [initialData]);

  const handleFieldEdit = (field) => {
    if (field === 'all') {
      setIsEditing(true);
    }
  };

  const handleFieldSave = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const productData = {
        ...formData,
        localPrice: parseFloat(formData.localPrice) || 0,
        // If image failed to load in preview, don't save it to DB to avoid broken images
        image: imageLoadError ? '' : formData.image
      };

      const newProduct = await addProduct(productData);

      if (newProduct) {
        // Automatically add 1 item to inventory for the newly created product
        // Note: Await is crucial here to ensure we get the true result
        const inventorySuccess = await adjustInventory(
          newProduct.id,
          1,
          'purchase',
          'Initial inventory from UPC scan',
          user?.name || user?.email || 'System'
        );
        
        if (inventorySuccess) {
          playNotificationSound('success');
          toast.success('Product added successfully! 1 item added to inventory.');
        } else {
          // If adjustInventory fails (returns false), we inform the user
          // But the product was still created
          playNotificationSound('error');
          toast.success('Product added successfully!');
          toast.error('Failed to add initial inventory item. Please update stock manually.');
        }
        onClose();
      } else {
        playNotificationSound('error');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      playNotificationSound('error');
      toast.error('Failed to add product');
    } finally {
      setLoading(false);
    }
  };

  const renderEditableField = (field, label, type = 'text') => {
    const value = formData[field];
    const isFieldEditing = isEditing;
    
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label} {field !== 'description' && <span className="text-red-500">*</span>}
        </label>
        {isFieldEditing ? (
          <div className="flex items-center space-x-2">
            <input
              type={type}
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field]: e.target.value }))}
              className={`flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${field === 'localPrice' ? 'pl-8' : ''}`}
              placeholder={field === 'localPrice' ? '0.00' : `Enter ${label.toLowerCase()}`}
              step={field === 'localPrice' ? '0.01' : undefined}
              min={field === 'localPrice' ? '0' : undefined}
            />
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="relative group">
            <div 
              className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-white ${field === 'localPrice' ? 'pl-8' : ''}`}
              onClick={() => setIsEditing(true)}
            >
              {value || (
                <span className="text-gray-400 dark:text-gray-500">
                  {field === 'localPrice' && '$'}Click to {field === 'upc' ? 'confirm' : 'edit'} {label.toLowerCase()}
                </span>
              )}
            </div>
            {field === 'localPrice' && value !== '' && (
              <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
            )}
            <SafeIcon icon={FiEdit2} className="absolute right-3 top-2 h-4 w-4 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          </div>
        )}
        {field === 'localPrice' && !isFieldEditing && value === '' && (
          <span className="absolute left-3 top-2 text-gray-500 dark:text-gray-400">$</span>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiPackage} className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add New Product</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    UPC: {initialData?.upc || 'Unknown'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors duration-200"
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              
              {/* Product Image Preview */}
              {formData.image && (
                <div className="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="text-center">
                    {formData.image.includes('http') && !imageLoadError ? (
                      <img 
                        src={formData.image} 
                        alt={formData.name || 'Product'} 
                        className="h-32 w-32 object-cover rounded-lg shadow-md"
                        onLoad={() => setImageLoadError(false)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          setImageLoadError(true);
                        }}
                      />
                    ) : (
                      <div className="h-32 w-32 bg-gray-200 dark:bg-gray-600 rounded-lg flex items-center justify-center">
                        <SafeIcon icon={FiImage} className="h-12 w-12 text-gray-400 dark:text-gray-500" />
                      </div>
                    )}
                    {imageLoadError && (
                       <p className="text-xs text-red-500 mt-2">Image failed to load</p>
                    )}
                    {!imageLoadError && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Product Image Preview</p>
                    )}
                  </div>
                </div>
              )}

              {/* Product Name (always visible and required) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter product name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderEditableField('manufacturer', 'Manufacturer')}
                {renderEditableField('model', 'Model')}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Product category"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Product description (optional)"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {renderEditableField('upc', 'UPC/Barcode')}
                {renderEditableField('localSku', 'Local SKU')}
              </div>

              {renderEditableField('localPrice', 'Local Price', 'number')}

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Product Image URL</label>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    value={formData.image}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, image: e.target.value }));
                      setImageLoadError(false); // Reset error on change to try again
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="https://example.com/product-image.jpg"
                  />
                  {imageLoadError && formData.image && (
                     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                       Broken URL
                     </span>
                  )}
                </div>
              </div>

              {/* Price Comparison from Retailers */}
              {formData.retailers && formData.retailers.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-3">Price Comparison</h4>
                  <div className="space-y-2">
                    {formData.retailers.map((retailer, index) => {
                      // Determine if this retailer has the lowest price
                      const prices = formData.retailers.map(r => r.price).filter(p => typeof p === 'number' && p > 0);
                      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
                      const isLowest = retailer.price === minPrice && minPrice > 0;

                      return (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-blue-700 dark:text-blue-400">
                            {retailer.name}
                            {isLowest && (
                              <span className="ml-2 text-xs bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 px-1.5 py-0.5 rounded-full border border-green-200 dark:border-green-800">
                                Lowest
                              </span>
                            )}
                          </span>
                          <span className={`font-medium ${isLowest ? 'text-green-700 dark:text-green-400 font-bold' : 'text-blue-900 dark:text-blue-200'}`}>
                            ${retailer.price?.toFixed(2) || 'N/A'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Specifications */}
              {formData.specifications && Object.keys(formData.specifications).length > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-green-900 dark:text-green-300 mb-3">Product Specifications</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(formData.specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-green-700 dark:text-green-400 capitalize">{key}:</span>
                        <span className="text-green-900 dark:text-green-200">
                          {Array.isArray(value) ? value.join(', ') : value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info Section */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiEdit2} className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-1">AI-Powered Product Information</h4>
                    <p className="text-sm text-amber-700 dark:text-amber-400">
                      Product information was automatically retrieved using AI search across the web. Click on any field to edit the information. All fields marked with * are required.
                    </p>
                    <div className="mt-2 p-2 bg-amber-100 dark:bg-amber-900/40 rounded">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-200">📦 <strong>Auto-Inventory:</strong> 1 item will be automatically added to inventory when this product is saved.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.name.trim()}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <SafeIcon icon={FiSave} className="h-4 w-4 mr-2" />
                  )}
                  Add Product (+1 Inventory)
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddProductFromUpcModal;